<script>
import { cloneDeep } from 'lodash-es';
import { mapState } from 'vuex';
import ValidatorMixin from '../../mixins/validatorMixin';

export default {
  name: 'ItineraryIncludeList',
  props: {
    itineraryId: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    includeItems: {
      type: Array,
      default: () => [],
    },
    selectedDomain: {
      type: Object,
      default: null,
    },
    includeCategories: {
      type: Array,
      default: () => [],
    },
    listTitle: {
      type: String,
      default: '',
    },
    includeNot: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localIncludeItems: [],
      headers: [
        { text: this.$t('itinerary.edit.include.table.no'), sortable: false, width: '5' },
        { text: this.$t('itinerary.edit.include.table.category'), sortable: false, width: '15' },
        { text: this.$t('itinerary.edit.include.table.headline'), sortable: false, width: '30' },
        { text: this.$t('itinerary.edit.include.table.text'), sortable: false, width: '30' },
        { text: this.$t('itinerary.edit.include.table.actions'), sortable: false, width: '20', align: 'center' },
      ],
      valid: true,
    };
  },
  created() {
    this.localIncludeItems = cloneDeep(this.includeItems);
  },
  computed: {
    ...mapState({
      domains: state => state.itinerary.domains,
    }),
  },
  methods: {
    save() {
      this.$emit('saveIncludes', this.localIncludeItems);
    },
    swap(a, b) {
      const i = this.localIncludeItems[a];
      this.$set(this.localIncludeItems, a, this.localIncludeItems[b]);
      this.$set(this.localIncludeItems, b, i);
    },
    moveItemUp(idx) {
      this.swap(idx, idx - 1);
    },
    moveItemDown(idx) {
      this.swap(idx, idx + 1);
    },
    sort() {
      if (this.localIncludeItems.length > 1) {
        // add includeCategories.priority for sorting
        this.includeCategories.forEach(
          (item) => {
            this.localIncludeItems.forEach(
              (incItem, idx) => {
                if (incItem.includeCategoryId === item.id) {
                  this.localIncludeItems[idx].includeCategoryPriority = item.priority;
                }
              },
            );
          },
        );

        let changes;
        do {
          changes = false;
          for (let i = 0; i < this.localIncludeItems.length - 1; i += 1) {
            if (this.localIncludeItems[i].includeCategoryPriority >
                this.localIncludeItems[i + 1].includeCategoryPriority) {
              this.swap(i, i + 1);
              changes = true;
            } else if (this.localIncludeItems[i].includeCategoryPriority ===
              this.localIncludeItems[i + 1].includeCategoryPriority &&
              this.localIncludeItems[i].headline > this.localIncludeItems[i + 1].headline) {
              this.swap(i, i + 1);
              changes = true;
            }
          }
        } while (changes);
      }
    },
    addItem() {
      const domains = {};

      this.domains.items.forEach(
        (domain) => {
          domains[domain.domainId] = { domainId: domain.domainId, headline: '', text: '' };
        },
      );

      this.localIncludeItems.push({ includeCategoryId: 1, headline: '', text: '', priority: 9999, domains });
    },
  },
  watch: {
    includeItems() {
      this.localIncludeItems = cloneDeep(this.includeItems);
    },
  },
  mixins: [ValidatorMixin],
};
</script>

<template>
  <v-container class="pa-0 fluid">
    <v-card-title class="pb-0">
      <div class="headline">{{listTitle}}</div>
    </v-card-title>
    <v-layout row wrap>
      <v-flex class="my-12">
        <v-form v-model="valid">
          <v-data-table
            :items="localIncludeItems"
            :headers="headers"
            :loading="loading"
            hide-actions
            class="elevation-1 edit-include"
          >
            <template v-slot:headers="props">
              <tr>
                <th
                  :key="index"
                  :style="{ 'width': `${header.width}%` }"
                  v-for="(header, index) in props.headers"
                  class="text-xs-left"
                >
                  {{ header.text }}
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr>
                <td>{{ $t('itinerary.edit.include.table.iterator', [props.index + 1]) }}</td>
                <td>
                  <v-select
                    v-model="props.item.includeCategoryId"
                    :items="includeCategories"
                    item-value="id"
                    item-text="description"
                    class="my-2"
                  >
                  </v-select>
                </td>
                <td>
                  <v-text-field
                    v-model="props.item.headline"
                    class="my-2"
                  />
                  <v-text-field
                    v-if="selectedDomain"
                    v-model="props.item.domains[''+selectedDomain.domainId].headline"
                    :label="$t('itinerary.edit.include.table.localHeadline')"
                    hide-details
                    class="my-2"
                  />
                </td>
                <td>
                  <v-textarea
                    v-model="props.item.text"
                    class="my-2"
                  />
                  <v-textarea
                    v-if="selectedDomain"
                    v-model="props.item.domains[''+selectedDomain.domainId].text"
                    :label="$t('itinerary.edit.include.table.localText')"
                    hide-details
                    class="my-2"
                  />
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :disabled="props.index == 0"
                    fab
                    small
                    depressed
                    color="primary"
                    @click="moveItemUp(props.index)"
                  >
                    <v-icon>arrow_upward</v-icon>
                  </v-btn>
                  <v-btn
                    :disabled="props.index == localIncludeItems.length-1"
                    fab
                    small
                    depressed
                    color="primary"
                    @click="moveItemDown(props.index)"
                  >
                    <v-icon>arrow_downward</v-icon>
                  </v-btn>
                  <v-btn
                    fab
                    small
                    depressed
                    color="error"
                    @click="localIncludeItems.splice(props.index, 1)"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
          <v-btn
            color="success"
            class="left mr-4 mt-4"
            @click="sort"
          >
            {{ $t('buttons.sort') }}
            <v-icon class="ml-1">sort</v-icon>
          </v-btn>
          <v-btn
            :loading="loading"
            color="primary"
            class="right mr-4 mt-4"
            @click="save"
          >
            {{ $t('buttons.save') }}
            <v-icon class="ml-1">save</v-icon>
          </v-btn>
          <v-btn
            :loading="loading"
            color="success"
            class="right mr-4 mt-4"
            @click="addItem"
          >
            {{ $t('buttons.add') }}
            <v-icon class="ml-1">add</v-icon>
          </v-btn>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" scoped>
  @import '../../assets/scss/variables.scss';

  .expanded-row {
    position: relative;
    display: table-row;
    height: $expanded-row-height;
    width: 100%;
    overflow: hidden;
  }

  .expanded-row-input {
    margin-top: $margin-lg;
    margin-left: $margin-lg;
    width: $expanded-row-input-width;
  }

  .expanded-row-data {
    position: absolute;
    width: 100%;
    height: $expanded-row-height;
    overflow: auto;
  }

  .expanded-row-form {
    width: 50%;
    float: right;
  }

  .edit-include td {
    vertical-align: top;
  }

  .edit-include .v-input {
    height:120px;
  }

  .edit-include td:first-child {
    padding-top: 15px;
  }

</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryIncludeList.vue